import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSSProperties, memo, useCallback, useMemo, useState } from 'react'
import { CSS } from '@dnd-kit/utilities'
import { ResumeSkill, ResumeSkillCategory } from 'packages/types'
import { closestCenter, DndContext, DragEndEvent } from '@dnd-kit/core'
import { useI18n } from 'builder/hooks/useI18n'
import { getCardHighlightId, SectionNames } from 'builder/modules/resumeEditor'
import { MouseClickEvent } from 'builder/modules/ui'
import { getSkillsByCategoryId } from 'builder/modules/resumeEditor/utils'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import Anchor from '../Anchor'
import DialogModal from '../DialogModal'
import { TextField } from '../TextField'
import { AsyncAutosuggest, createSuggestionsApiFetcher } from '../AsyncAutosuggest'
import { TooltipV2 } from '../Tooltip'
import { Icon20 } from '../Icon'
import AddCardButton from '../AddCardButton'
import { CardComponentProps } from './types'
import { useCardHandlers } from './hooks/useCardHandlers'
import {
  CardContentWrapper,
  Field,
  Row,
  SubItemCardContentWrapper,
  SubItemContainer,
  SubItemDeleteIcon,
  SubItemDragIcon,
  SubItemsContainer,
  Wrapper,
} from './styles'
import { Base } from './Base'

const SkillInsideCategory = memo(
  (props: CardComponentProps<ResumeSkill> & { sortId: string | number }) => {
    const { sortId, item, autoFocus, locale, onDelete, disableMove, isDeleteVisible } = props

    const { isPhone } = useMediaQueries()

    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
      id: sortId,
    })

    const style: CSSProperties = {
      transform: CSS.Transform.toString(transform ? { ...transform, scaleX: 1, scaleY: 1 } : null),
      transition,
      position: 'relative',
      zIndex: isDragging ? 1 : undefined,
    }

    const { i18n } = useI18n()
    const { updateSimpleField } = useCardHandlers(props)
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

    const showDeleteModal = (event?: MouseClickEvent) => {
      if (event) event.stopPropagation()
      setIsDeleteModalOpen(true)
    }

    const handleModalConfirm = () => {
      setIsDeleteModalOpen(false)
      onDelete(item.id, SectionNames.skills)
    }

    const handleModalCancel = () => {
      setIsDeleteModalOpen(false)
    }

    return (
      <div ref={setNodeRef} style={style} {...attributes}>
        <Anchor id={sortId}>
          <Wrapper>
            <SubItemContainer
              isDragging={isDragging}
              data-highlight-id={getCardHighlightId(SectionNames.skills, item.cid || item.id)}
            >
              <DialogModal
                title={i18n.t('builder.resume_editor.delete_entry')}
                text={i18n.t('builder.resume_editor.sure_to_delete')}
                primaryButtonText={i18n.t('builder.resume_editor.delete')}
                secondaryButtonText={i18n.t('builder.resume_editor.cancel')}
                dontShowAgainId="deleteCardDialog2" // optional
                isOpen={isDeleteModalOpen}
                primaryAction={handleModalConfirm}
                secondaryAction={handleModalCancel}
                closeAction={handleModalCancel}
              />

              <SubItemCardContentWrapper>
                <Row>
                  <Field fullwidth>
                    <AsyncAutosuggest
                      name="skill"
                      fetchItems={createSuggestionsApiFetcher('skill', { locale })}
                      value={item.skill || ''}
                      onChange={updateSimpleField}
                    >
                      {({ key, ...inputProps }) => (
                        <TextField
                          {...inputProps}
                          key={key}
                          autoFocus={autoFocus && !item.skill}
                          label={i18n.t('builder.resume_editor.skills_card.skill')}
                        />
                      )}
                    </AsyncAutosuggest>
                  </Field>
                </Row>
              </SubItemCardContentWrapper>

              {!disableMove && <SubItemDragIcon isDragging={isDragging} {...listeners} />}

              <TooltipV2 value={i18n.t('builder.resume_editor.delete')} position="top">
                <SubItemDeleteIcon
                  onClick={showDeleteModal}
                  isDeleteVisible={isDeleteVisible || isPhone}
                >
                  <Icon20.Trash />
                </SubItemDeleteIcon>
              </TooltipV2>
            </SubItemContainer>
          </Wrapper>
        </Anchor>
      </div>
    )
  },
)

export const SkillCategory = memo((props: CardComponentProps<ResumeSkillCategory, ResumeSkill>) => {
  const { item, autoFocus, onAddSubItem, possibleSubItems, onSubItemMove, sectionId } = props
  const { i18n } = useI18n()
  const { updateSimpleField } = useCardHandlers(props)
  const title = item.title || i18n.t('builder.resume_editor.not_specified')
  const cardId = item.cid || item.id

  const subItems = useMemo(() => {
    if (!possibleSubItems) return []
    return getSkillsByCategoryId(possibleSubItems, item.id)
  }, [possibleSubItems, item.id])

  const addButtonText = subItems.length
    ? i18n.t('builder.resume_editor.skills_section.add_one_more')
    : i18n.t('builder.resume_editor.skills_section.add')

  const cardsOrder = useMemo(() => {
    return subItems.map(item => item.cid || item.id || '')
  }, [subItems])

  const onDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      if (!over || active.id === over.id || !onSubItemMove || !cardId) return

      const oldIndex = cardsOrder.indexOf(active.id)
      const newIndex = cardsOrder.indexOf(over.id)

      onSubItemMove({ oldIndex, newIndex, sectionId, cardId })
    },
    [onSubItemMove, cardsOrder, sectionId, cardId],
  )

  const handleAddSubItem = () => {
    onAddSubItem?.(item.id || item.cid, SectionNames.skillCategories)
  }

  return (
    <Base {...props} title={title}>
      <CardContentWrapper>
        <Row>
          <Field fullwidth>
            <TextField
              autoFocus={autoFocus}
              label={i18n.t('builder.resume_editor.skills_card.title')}
              name="title"
              onChange={updateSimpleField}
              value={item.title || ''}
            />
          </Field>
        </Row>

        <SubItemsContainer>
          <DndContext onDragEnd={onDragEnd} collisionDetection={closestCenter}>
            <SortableContext items={cardsOrder} strategy={verticalListSortingStrategy}>
              {subItems?.map(item => {
                const sortId = item.cid || item.id
                if (!sortId) return null
                return (
                  <SkillInsideCategory
                    {...props}
                    sortId={sortId}
                    key={sortId}
                    sectionId={SectionNames.skills}
                    item={item}
                  />
                )
              })}
            </SortableContext>
          </DndContext>
        </SubItemsContainer>

        <Row>
          <AddCardButton onAdd={handleAddSubItem} text={addButtonText} />
        </Row>
      </CardContentWrapper>
    </Base>
  )
})
